<template>
  <div>
    <input
      v-if="candidates.length > SEARCH_THRESHOLD"
      v-model="search"
      type="search"
      class="form-control form-control-sm mb-2"
      :placeholder="$t('forwards.pools.members.search')"
      :aria-label="$t('forwards.pools.members.search')"
    />

    <ul class="members-list list-unstyled mb-0">
      <li
        v-for="candidate in visibleCandidates"
        :key="candidate.company_id"
        class="members-list__row"
      >
        <div class="form-check mb-0">
          <input
            :id="`${listId}-${candidate.company_id}`"
            class="form-check-input"
            type="checkbox"
            :checked="isSelected(candidate.company_id)"
            :disabled="disabled || isBlocked(candidate)"
            @change="toggle(candidate.company_id)"
          />
          <label
            class="form-check-label"
            :for="`${listId}-${candidate.company_id}`"
          >
            <span
              v-if="candidate.country"
              class="me-1"
              :title="translateCountryCode(candidate.country, t, te)"
              >{{ getFlagEmoji(candidate.country) }}</span
            >{{ candidate.company_name }}
          </label>
        </div>
        <div v-if="isBlocked(candidate)" class="members-list__hint">
          {{ $t("forwards.pools.members.not_enabled") }}
        </div>
        <div v-else-if="!candidate.allocation_ready" class="members-list__hint">
          {{ $t("forwards.pools.members.not_allocation_ready") }}
        </div>
      </li>
      <li
        v-if="visibleCandidates.length === 0"
        class="text-body-tertiary fs-10 py-2"
      >
        {{ $t("forwards.pools.members.no_candidates") }}
      </li>
    </ul>

    <p v-if="modelValue.length === 0 && touched" class="error-note mt-3 mb-0">
      <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
      <span>{{ $t("forwards.pools.members.at_least_one") }}</span>
    </p>

    <p v-for="blocker in blockers" :key="blocker" class="error-note mt-2 mb-0">
      <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
      <span>{{ blocker }}</span>
    </p>
  </div>
</template>

<script setup lang="ts">
import type { PoolMemberRow } from "@/forwards2/forwardsTypes";
import { getFlagEmoji, translateCountryCode } from "@/utils/countries";
import { sortBy } from "lodash-es";
import { computed, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";

/**
 * Which of the caller's companies sell through this pool.
 *
 * D-09a-01: a filterable checkbox list rather than `SearchableSelectComponent`, because a company
 * without `forward_sales_enabled` has to be *visible and disabled* with the reason next to it — a
 * combobox can only leave it out, which is exactly the confusion the requirement exists to avoid.
 *
 * Removing a member can be refused by the backend when the pool would no longer cover what it owes;
 * `blockers` carries that explanation from the preview, unchanged.
 */
const props = withDefaults(
  defineProps<{
    /** Every company the actor could attach, with the two flags that decide whether it may. */
    candidates: PoolMemberRow[];
    /** Save-blocking reasons from `POST /pools/preview`, rendered verbatim. */
    blockers?: string[];
    disabled?: boolean;
  }>(),
  { blockers: () => [], disabled: false }
);

const modelValue = defineModel<number[]>({ required: true });

/** Below this many candidates the search box is noise. */
const SEARCH_THRESHOLD = 8;

const { t, te } = useI18n();
const listId = useId();
const search = ref("");
// Ticking a box must not move rows under the cursor, so the order follows the selection as it was
// when the candidate list last arrived — on load and after a save — not as it is now.
const selectedWhenListed = ref(new Set<number>());
// A new pool opens with no company yet, which is the user's to fill in, not a mistake to flag; the
// missing-company note waits until a selection has been made and emptied again.
const touched = ref(modelValue.value.length > 0);

const orderedCandidates = computed(() =>
  sortBy(props.candidates, [
    (candidate) => !selectedWhenListed.value.has(candidate.company_id),
    (candidate) => !candidate.forward_sales_enabled,
    (candidate) => candidate.company_name.toLowerCase(),
  ])
);

const visibleCandidates = computed(() => {
  const query = search.value.trim().toLowerCase();
  if (query === "") return orderedCandidates.value;
  return orderedCandidates.value.filter((candidate) =>
    candidate.company_name.toLowerCase().includes(query)
  );
});

watch(
  () => modelValue.value.length,
  (count) => {
    if (count > 0) touched.value = true;
  }
);

watch(
  () => props.candidates,
  () => {
    selectedWhenListed.value = new Set(modelValue.value);
  },
  { immediate: true }
);

const isSelected = (companyId: number) => modelValue.value.includes(companyId);

/**
 * Whether this company cannot be attached. The flag gates attaching only, so a member whose
 * `forward_sales_enabled` was revoked after it joined stays removable — a disabled checkbox would
 * otherwise leave the pool unsavable with nothing to click (RV2-pool-lifecycle-04).
 */
const isBlocked = (candidate: PoolMemberRow) =>
  !candidate.forward_sales_enabled && !isSelected(candidate.company_id);

const toggle = (companyId: number) => {
  modelValue.value = isSelected(companyId)
    ? modelValue.value.filter((id) => id !== companyId)
    : [...modelValue.value, companyId];
};
</script>

<style scoped lang="scss">
.members-list {
  max-height: 16rem;
  overflow-y: auto;
}

.members-list__row {
  padding: 0.25rem 0;

  & + & {
    border-top: 1px solid var(--phoenix-border-color-translucent);
  }
}

.members-list__hint {
  font-size: 0.6875rem;
  color: var(--phoenix-gray-600);
  padding-left: 1.5rem;
}
</style>
